<template>
  <b-modal
    id="close-shift-modal"
    ref="modal"
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    centered
  >
    <template #modal-title>
      <span class="font-weight-bold">
        <i class="bi bi-cash-coin mr-1"></i> Close Shift Cash
      </span>
    </template>

    <div class="px-2 pb-2">

      <!-- Loading state -->
      <div v-if="loadingSession" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        <p class="text-muted mt-2 mb-0" style="font-size:13px;">Loading session…</p>
      </div>

      <!-- No active session -->
      <div v-else-if="!session" class="text-center py-4">
        <i class="bi bi-inbox text-secondary" style="font-size:32px;"></i>
        <p class="text-muted mt-2 mb-1" style="font-size:13px;">You don't have an active cash session to close.</p>
        <button @click="hide" class="btn btn-sm btn-light border mt-2" style="border-radius:6px;">Close</button>
      </div>

      <!-- Submitted state -->
      <div v-else-if="submitted" class="text-center py-4">
        <i class="bi bi-check-circle text-success" style="font-size:40px;"></i>
        <h6 class="mt-3 mb-1">Closing Submitted</h6>
        <p class="text-muted mb-3" style="font-size:13px;">
          Your cash closing has been submitted and is awaiting manager approval.
        </p>
        <button @click="hide" class="btn btn-sm btn-dark" style="border-radius:6px;font-size:13px;">Done</button>
      </div>

      <!-- Close form -->
      <div v-else>
        <!-- Session summary -->
        <div
          class="mb-4 p-3"
          style="background:#f8fafc;border-radius:8px;border:1px solid #e9ecef;"
        >
          <div class="d-flex justify-content-between mb-1">
            <span class="text-secondary" style="font-size:12px;">Session opened</span>
            <span class="text-dark" style="font-size:12px;font-weight:500;">{{ formatTime(session.opened_at) }}</span>
          </div>
          <div v-if="session.shift" class="d-flex justify-content-between mb-1">
            <span class="text-secondary" style="font-size:12px;">Shift</span>
            <span class="text-dark" style="font-size:12px;font-weight:500;">{{ session.shift.name }}</span>
          </div>
          <div class="d-flex justify-content-between mb-1">
            <span class="text-secondary" style="font-size:12px;">Opening cash</span>
            <span class="text-dark" style="font-size:12px;font-weight:500;">{{ formatCurrency(session.opening_amount) }}</span>
          </div>
          <div class="d-flex justify-content-between">
            <span class="text-secondary" style="font-size:12px;">Expected in till</span>
            <span class="text-dark" style="font-size:12px;font-weight:600;">
              {{ session.expected_cash_preview != null ? formatCurrency(session.expected_cash_preview) : '—' }}
            </span>
          </div>
        </div>

        <form @submit.prevent="submit">
          <!-- Actual cash input -->
          <div class="form-group">
            <label class="font-weight-bold" style="font-size:13px;">
              Actual Cash in Till <span class="text-danger">*</span>
            </label>
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <input
                ref="amountInput"
                type="number"
                class="form-control form-control-lg"
                v-model.number="form.closing_amount"
                min="0"
                step="0.01"
                placeholder="0.00"
                required
                :disabled="submitting"
              />
            </div>
          </div>

          <!-- Live variance preview -->
          <div
            v-if="form.closing_amount !== null && session.expected_cash_preview != null"
            class="mb-3 px-3 py-2"
            style="border-radius:6px;font-size:13px;"
            :style="varianceStyle"
          >
            <span class="font-weight-bold">Variance: </span>
            {{ variance >= 0 ? '+' : '' }}{{ formatCurrency(variance) }}
            <span class="ml-1 text-muted" style="font-size:11px;">({{ variancePct }}%)</span>
          </div>

          <!-- Notes -->
          <div class="form-group">
            <label style="font-size:13px;">Notes <small class="text-muted">(optional)</small></label>
            <textarea
              class="form-control"
              v-model="form.notes"
              rows="2"
              maxlength="500"
              placeholder="e.g. Short by KES 200 — change given to customer, forgot to log…"
              :disabled="submitting"
            ></textarea>
          </div>

          <div v-if="error" class="alert alert-danger py-2 mb-3" style="font-size:13px;">
            {{ error }}
          </div>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <button
              type="button"
              @click="hide"
              class="btn btn-sm btn-light border"
              style="border-radius:6px;"
              :disabled="submitting"
            >Cancel</button>
            <button
              type="submit"
              class="btn btn-primary px-4"
              :disabled="submitting || form.closing_amount === null || form.closing_amount < 0"
            >
              <span v-if="submitting">Submitting…</span>
              <span v-else>Submit for Approval</span>
            </button>
          </div>
        </form>
      </div>

    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'CloseShiftModal',

  data() {
    return {
      loadingSession: false,
      session: null,
      submitted: false,
      submitting: false,
      error: null,
      form: {
        closing_amount: null,
        notes: ''
      }
    };
  },

  computed: {
    currency() {
      return this.$store.state.userDetails?.company?.companysettings?.currency || 'KES';
    },

    variance() {
      if (this.form.closing_amount === null || this.session?.expected_cash_preview == null) return 0;
      return this.form.closing_amount - this.session.expected_cash_preview;
    },

    variancePct() {
      const expected = this.session?.expected_cash_preview;
      if (!expected) return '0.00';
      return Math.abs((this.variance / expected) * 100).toFixed(2);
    },

    varianceStyle() {
      if (this.variance === 0) return 'background:#f8fafc;border:1px solid #e9ecef;color:#6c757d;';
      if (this.variance > 0) return 'background:#d4edda;border:1px solid #c3e6cb;color:#155724;';
      return 'background:#f8d7da;border:1px solid #f5c6cb;color:#721c24;';
    }
  },

  mounted() {
    this.$root.$on('open-close-shift-modal', this.open);
  },

  beforeDestroy() {
    this.$root.$off('open-close-shift-modal', this.open);
  },

  methods: {
    async open() {
      this.session      = null;
      this.submitted    = false;
      this.error        = null;
      this.form         = { closing_amount: null, notes: '' };
      this.loadingSession = true;
      this.$refs.modal.show();

      try {
        const { data } = await axios.get('/api/cash-sessions/active');
        this.session = data;
      } catch (_) {
        this.session = null;
      } finally {
        this.loadingSession = false;
        if (this.session) {
          this.$nextTick(() => this.$refs.amountInput?.focus());
        }
      }
    },

    hide() {
      this.$refs.modal.hide();
    },

    async submit() {
      this.error     = null;
      this.submitting = true;

      try {
        await axios.post(`/api/cash-sessions/${this.session.id}/close`, {
          closing_amount: this.form.closing_amount,
          notes: this.form.notes || null
        });

        this.$store.commit('clearCashSession');
        this.submitted = true;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to submit closing. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    formatCurrency(value) {
      if (value == null) return '—';
      const abs = Math.abs(value);
      const fmt = this.currency + ' ' + abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return value < 0 ? '-' + fmt : fmt;
    },

    formatTime(ts) {
      if (!ts) return '—';
      return new Date(ts).toLocaleString('en-KE', {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    }
  }
};
</script>
